import { useNavigate } from "react-router-dom";
import {
  Combobox,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
  Transition,
} from "@headlessui/react";
import React, { Fragment, useMemo, useState } from "react";
import classNames from "classnames";
import { ChevronUpDownIcon } from "@heroicons/react/24/outline";
import Button from "../Button";
import { FormLabel } from "../Input/FormLabel";

type InputSize = "lg" | "md" | "sm";

interface IconObject {
  left?: React.ReactNode;
  right?: React.ReactNode;
}

export interface DropdownItem {
  _id?: string;
  id?: string;
  name?: string;
  title?:
    | React.ReactNode
    | ((args: { item: DropdownItem; active: boolean }) => React.ReactNode);
  label?: React.ReactNode;
  icon?: React.ReactNode;
  disabled?: boolean;
  path?: string;
  onClick?: (item: DropdownItem) => void;
  [key: string]: unknown;
}

interface InputDropdownProps {
  data: DropdownItem[];
  placeholder?: string;
  size?: InputSize;
  className?: string;
  wrapperClasses?: string;
  dataWrapperClassName?: string;
  label?: React.ReactNode;
  labelClasses?: string;
  id?: string;
  required?: boolean;
  icon?: React.ReactElement | IconObject;
  value?: DropdownItem | null;
  onChange?: (value: string) => void;
  onSelect?: (item: DropdownItem) => void;
  footerButton?: React.ReactNode;
  footerButtonClick?: React.MouseEventHandler<HTMLButtonElement>;
  renderItem?: (args: {
    selected: boolean;
    active: boolean;
    item: DropdownItem;
  }) => React.ReactNode;
}

export const InputDropdown: React.FC<InputDropdownProps> = (props) => {
  const [internalSelected, setInternalSelected] = useState<DropdownItem | null>(
    null,
  );
  const [query, setQuery] = useState<string>("");

  const navigate = useNavigate();
  const isControlled = props.value !== undefined;
  const selected = isControlled ? props.value ?? null : internalSelected;

  const filteredData: DropdownItem[] = useMemo(() => {
    const source = props.data || [];
    const needle = query.trim().toLowerCase().replace(/\s+/g, "");

    if (!needle) {
      return source;
    }

    return source.filter((item) =>
      (item.name ?? "")
        .toLowerCase()
        .replace(/\s+/g, "")
        .includes(needle),
    );
  }, [props.data, query]);

  function handleSelect(item: DropdownItem | null) {
    if (!item || item.disabled) {
      return;
    }

    if (!isControlled) {
      setInternalSelected(item);
    }

    setQuery("");
    props.onSelect?.(item);
  }

  function handleClick(item: DropdownItem) {
    if (item.onClick) {
      item.onClick(item);
      return;
    }

    if (item.path) {
      navigate(item.path);
    }
  }

  function renderDefaultItem({
    item,
    active,
    selected: isSelected,
  }: {
    item: DropdownItem;
    active: boolean;
    selected: boolean;
  }) {
    const title = item.title || item.label;
    const itemClasses = classNames(
      "group flex w-full items-center rounded-md px-2 py-2 text-sm cursor-pointer",
      {
        "bg-primary-50 text-primary-900": active,
        "bg-white text-gray-900": !active,
        "font-medium text-primary-700": isSelected,
        "text-gray-400 !cursor-not-allowed": item.disabled,
      },
    );

    return (
      <div
        onClick={item.disabled ? undefined : () => handleClick(item)}
        className={itemClasses}
      >
        <span className="mr-1.5">{item.icon}</span>
        {typeof title === "function" ? title({ item, active }) : title}
      </div>
    );
  }

  const labelClasses = classNames(
    "block mb-1",
    "text-sm font-medium text-gray-900",
    props.labelClasses,
  );

  const sizeLg = props.size === "lg";
  const sizeMd = props.size === "md" || !props.size;
  const sizeSm = props.size === "sm";

  const isIconReactElement = props.icon
    ? React.isValidElement(props.icon)
    : false;

  const inputClasses = classNames(
    "shadow-sm bg-white block w-full rounded-lg border pr-10",
    "focus:outline-none focus-visible:ring-1 focus:ring-primary-500 focus:border-primary-500",
    "border-gray-300 text-gray-900 placeholder:text-gray-400",
    { "p-4 text-md": sizeLg },
    { "p-2.5 text-sm": sizeMd },
    { "p-2 text-xs": sizeSm },
    {
      "pl-8": !!(
        props.icon &&
        !isIconReactElement &&
        (props.icon as IconObject).left
      ),
    },
    props.className,
  );

  const wrapperClasses = classNames("block w-full", props.wrapperClasses);

  return (
    <div className={wrapperClasses}>
      {props.label && (
        <FormLabel
          htmlFor={props.id}
          required={props.required}
          className={labelClasses}
        >
          {props.label}
        </FormLabel>
      )}

      <Combobox value={selected} onChange={handleSelect} nullable>
        <div className="relative">
          <ComboboxInput
            id={props.id}
            placeholder={props.placeholder}
            className={inputClasses}
            displayValue={(item: DropdownItem | null) => item?.name || ""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const nextQuery = event.target.value;
              setQuery(nextQuery);
              props.onChange?.(nextQuery);
            }}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-3 text-gray-400 hover:text-gray-600">
            <ChevronUpDownIcon className="h-4 w-4" aria-hidden="true" />
          </ComboboxButton>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
            afterLeave={() => setQuery("")}
          >
            <ComboboxOptions
              className={classNames(
                "absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1",
                "text-sm shadow-lg ring-1 ring-black/5 focus:outline-none",
                props.dataWrapperClassName,
              )}
            >
              {filteredData.length === 0 ? (
                <div className="cursor-default select-none px-3 py-2 text-sm text-gray-500">
                  {query ? "No matches found." : "No options available."}
                </div>
              ) : (
                filteredData.map((item) => (
                  <ComboboxOption
                    key={(item._id as string) || (item.id as string)}
                    value={item}
                    disabled={Boolean(item.disabled)}
                    className="relative cursor-pointer select-none px-1 py-0.5"
                  >
                    {({ selected: isSelected, active }) => (
                      <div>
                        {props.renderItem
                          ? props.renderItem({
                              selected: isSelected,
                              active,
                              item,
                            })
                          : renderDefaultItem({
                              item,
                              active,
                              selected: isSelected,
                            })}
                      </div>
                    )}
                  </ComboboxOption>
                ))
              )}
            </ComboboxOptions>
          </Transition>
        </div>
      </Combobox>

      {props.footerButton && (
        <div className="flex justify-start mt-2">
          <Button type="link" onClick={props.footerButtonClick}>
            {props.footerButton}
          </Button>
        </div>
      )}
    </div>
  );
};

export default InputDropdown;
